/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '../../hooks/useQuery';

import {
  clearOnboardingFormById,
  createOnboardingSubmission,
  getOnboardingById,
  getOnboardingFormById,
  stopOnboardingEdit,
} from '../../data/store/onboarding/onboardingActions';

import { Layout } from 'antd';

import OnboardingSideBar from '../Onboarding/OnboardingSideBar';
import OnboardingCategory from '../Onboarding/OnboardingCategory';
import CategoryItem from '../Onboarding/CategoryItem';

const OnboardingCreateEdit = ({ theme }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const dispatch = useDispatch();
  const { onboardingId } = useParams();
  const query = useQuery(search);

  const { onboardingFormById, onboardingById } = useSelector(
    (state) => state.onboardingReducer,
  );

  const userId = query.get('account-id');
  const formId = query.get('form-id');
  const category = query.get('category');
  const subCategory = query.get('sub-category');

  const language = i18n.languages?.length ? i18n.languages[0] : 'en';
  let currentLang = ['en', 'sw'].includes(language) ? language : 'en';
  currentLang = currentLang === 'sw' ? 'sv' : currentLang;

  useEffect(() => {
    if (formId) {
      dispatch(getOnboardingFormById({ formId, language: currentLang }));
    }
    return () => {
      if (onboardingById?.editing_at) {
        dispatch(stopOnboardingEdit(onboardingById?.id));
      }
      dispatch(clearOnboardingFormById());
    };
  }, [formId]);

  useEffect(() => {
    if (onboardingId && onboardingId !== 'new' && !onboardingById) {
      dispatch(getOnboardingById(onboardingId));
    } else if (onboardingId === 'new') {
      dispatch(
        createOnboardingSubmission({
          account_id: +userId,
          form_id: formId,
        }),
      );
    }
  }, [onboardingId]);

  const categoryNavRef = useRef({ onboardingId: null, queryKey: null });

  useEffect(() => {
    if (!onboardingId || onboardingId === 'new') {
      return;
    }
    const queryKey = `${category ?? ''}|${subCategory ?? ''}`;
    const snap = categoryNavRef.current;

    if (snap.onboardingId !== onboardingId) {
      snap.onboardingId = onboardingId;
      snap.queryKey = queryKey;
      return;
    }

    if (snap.queryKey !== queryKey) {
      snap.queryKey = queryKey;
      dispatch(getOnboardingById(onboardingId));
    }
  }, [onboardingId, category, subCategory, dispatch]);

  useEffect(() => {
    if (onboardingById && onboardingId === 'new' && onboardingById.id) {
      const newPathname = pathname.replace('/new', `/${onboardingById.id}`);
      history.replace({
        pathname: newPathname,
        search: search,
      });
    }
  }, [onboardingById, onboardingId, pathname, search, history]);

  const onboardingPoints = useMemo(() => {
    const category = onboardingFormById?.form_element_groups
      ?.filter((i) => i.parent_form_element_group_id === null)
      ?.sort((a, b) => a.group_order - b.group_order);

    const categorySub = category?.map((i) => {
      return {
        ...i,
        subCategory: onboardingFormById?.form_element_groups
        ?.filter((j) => j.parent_form_element_group_id === i.id)
        ?.sort((a, b) => a.group_order - b.group_order),
      };
    });

    const optionsMaping = (id) => {
      return onboardingFormById?.static_options?.find(
        (i) => i.form_input_element_id === id,
      );
    };

    const withoutSub = categorySub?.map((i) => {
      if (i.subCategory?.length > 0) {
        return i;
      } else {
        return {
          ...i,
          form_elements: onboardingFormById?.form_elements
          ?.filter((j) => j.form_element_group_id === i.id)
          ?.map((el) => {
            if (el?.form_input_element?.id) {
                const options = optionsMaping(el?.form_input_element?.id);
                return options ? { ...el, options } : el;
              } else if (el?.form_composite_input_element?.id) {
                return {
                  ...el,
                  form_composite_input_element: {
                    ...el.form_composite_input_element,
                    form_composite_input_element_items:
                      el?.form_composite_input_element?.form_composite_input_element_items?.map(
                        (item) => {
                          const options = optionsMaping(
                            item?.form_input_element?.id,
                          );
                          return options ? { ...item, options } : item;
                        },
                      ),
                    },
                };
              } else {
                return el;
              }
            })
            ?.sort((a, b) => a.element_order - b.element_order),
          };
        }
      });

    const categorySubElements = withoutSub?.map((i) => {
      return {
        ...i,
        subCategory: i.subCategory.map((sub) => {
          return {
            ...sub,
            form_elements: onboardingFormById?.form_elements
              ?.filter((j) => j.form_element_group_id === sub.id)
              ?.map((el) => {
                if (el?.form_input_element?.id) {
                  const options = optionsMaping(el?.form_input_element?.id);
                  return options ? { ...el, options } : el;
                } else if (el?.form_composite_input_element?.id) {
                  return {
                    ...el,
                    form_composite_input_element: {
                      ...el.form_composite_input_element,
                      form_composite_input_element_items:
                        el?.form_composite_input_element?.form_composite_input_element_items?.map(
                          (item) => {
                            const options = optionsMaping(
                              item?.form_input_element?.id,
                            );
                            return options ? { ...item, options } : item;
                          },
                        ),
                    },
                  };
                } else {
                  return el;
                }
              })
              ?.sort((a, b) => a.element_order - b.element_order),
          };
        }),
      };
    });
    //TODO: add mcok
    return categorySubElements;
  }, [onboardingFormById]);

  const wayPoint = useMemo(() => {
    if (onboardingPoints) {
      const array = [];
      onboardingPoints.forEach((i) => {
        if (i.subCategory.length > 0) {
          i.subCategory.forEach((j) => {
            array.push(j.id);
          });
        } else {
          array.push(i.id);
        }
      });
      return array;
    } else {
      return [];
    }
  }, [onboardingPoints]);
  
  const validateCategory = useMemo(() => {
    console.log('🚀 ~ OnboardingCreateEdit ~ onboardingPoints:', onboardingPoints);
    let result = {};
    onboardingPoints?.forEach((i) => {
      result[i.id] = {mandatory: 0, total: 0, filledMandatory: 0, filledTotal: 0, mandatoryArray: [], subCategory: []};
      if(i?.subCategory?.length) {
        i.subCategory.forEach((j) => {
          const mandatoryElements = j.form_elements
            .filter((k) => k.form_input_element?.min_number_of_submissions === 1 ||
              k.form_composite_input_element?.min_number_of_submissions === 1)
                ?.map(el => el.form_input_element?.id || el.form_composite_input_element?.id);
          const countMandatory = mandatoryElements.length ?? 0;
          const countTotal = j.form_elements
            ?.filter(el => el.form_input_element?.submit_value_Type !== 'NONE' && 
              !el?.form_action_element
            ).length ?? 0;
          result[i.id].mandatory += countMandatory;
          result[i.id].total += countTotal;
          result[i.id].mandatoryArray = [...result[i.id].mandatoryArray, ...mandatoryElements];
          
          let subCategory = {id: j.id, mandatory: 0, total: 0, filledTotal: 0,mandatoryArray: []};
          subCategory.mandatory = countMandatory;
          subCategory.total = countTotal;
          subCategory.mandatoryArray = [ ...mandatoryElements];
          subCategory.filledMandatory = 0;
          subCategory.filledTotal = 0;

          
          j.form_elements.forEach(el => {
            const stored = onboardingById?.form_input_element_submissions
            ?.find(i => i.form_input_element_id === el?.form_input_element?.id) || onboardingById?.form_composite_input_element_submissions
            ?.find(i => i.form_composite_input_element_id === el?.form_composite_input_element?.id);
            if(stored) {
              subCategory.filledTotal++;
            }
          });
          result[i.id].filledTotal += subCategory.filledTotal;

          subCategory.mandatoryArray?.forEach(el => {
            const isFilled = onboardingById?.form_input_element_submissions
              ?.find(i => i.form_input_element_id === el) || onboardingById?.form_composite_input_element_submissions
              ?.find(i => i.form_composite_input_element_id === el);
            if(isFilled) {
              subCategory.filledMandatory++;
            }
          });

          result[i.id].subCategory.push(subCategory);
        });
      } else {
        const mandatoryElements = i.form_elements
          .filter((k) => k.form_input_element?.min_number_of_submissions === 1 ||
            k.form_composite_input_element?.min_number_of_submissions === 1)
            ?.map(el => el.form_input_element?.id || el.form_composite_input_element?.id);
            const countMandatory = mandatoryElements.length ?? 0;
        const countTotal = i.form_elements
          ?.filter(el => el.form_input_element?.submit_value_Type !== 'NONE' && 
            !el?.form_action_element
          ).length ?? 0;
        result[i.id].mandatory += countMandatory;
        result[i.id].total += countTotal;
        result[i.id].mandatoryArray = [...result[i.id].mandatoryArray, ...mandatoryElements];
        let filledTotal= 0;
        i.form_elements.forEach(el => {
          const stored = onboardingById?.form_input_element_submissions
            ?.find(i => i.form_input_element_id === el?.form_input_element?.id) || onboardingById?.form_composite_input_element_submissions
            ?.find(i => i.form_composite_input_element_id === el?.form_composite_input_element?.id);
          console.log('🚀 ~ OnboardingCreateEdit ~ stored:', stored);
          if(stored) {
            filledTotal++;
          }
        });
        console.log('🚀 ~ OnboardingCreateEdit ~ filledTotal:', filledTotal);
        result[i.id].filledTotal += filledTotal;
      }
      result[i.id].mandatoryArray?.forEach(el => {
        const isFilled = onboardingById?.form_input_element_submissions
          ?.find(i => i.form_input_element_id === el) || onboardingById?.form_composite_input_element_submissions
          ?.find(i => i.form_composite_input_element_id === el);
        if(isFilled) {
          result[i.id].filledMandatory++;
        }
      });
    });

    return result;
  }, [onboardingPoints, onboardingById]);

  useEffect(() => {
    console.log('validateCategory', validateCategory);
    
  }, [validateCategory]);
  
  
  const getItemForCategory = useMemo(() => {
    const data = onboardingPoints?.find((i) => i.id === category);
    return data;
  }, [category, subCategory, onboardingPoints]);

  useEffect(() => {
    if (!onboardingPoints?.length) {
      return;
    }

    const newQuery = new URLSearchParams(search);
    let needsReplace = false;

    const rootCategory = onboardingPoints.find((p) => p.id === category);
    const categoryValid = Boolean(rootCategory);

    if (!categoryValid) {
      newQuery.set('category', onboardingPoints[0].id);
      newQuery.delete('sub-category');
      needsReplace = true;
    } else {
      const subs = rootCategory.subCategory || [];
      if (subs.length > 0) {
        if (subCategory && !subs.some((s) => s.id === subCategory)) {
          newQuery.delete('sub-category');
          needsReplace = true;
        }
      } else if (subCategory) {
        newQuery.delete('sub-category');
        needsReplace = true;
      }
    }

    if (needsReplace) {
      history.replace({
        pathname,
        search: `?${newQuery.toString()}`,
      });
    }
  }, [
    onboardingId,
    formId,
    onboardingPoints,
    category,
    subCategory,
    search,
    pathname,
    history,
  ]);

  const handleSelectCategory = (id) => {
    if (id === category) {
      return;
    }
    const newQuery = new URLSearchParams(search);
    newQuery.set('category', id);
    newQuery.delete('sub-category');
    history.replace({
      pathname,
      search: `?${newQuery.toString()}`,
    });
  };

  return (
    <Layout className="layout-main onbording-card-wrapper">
      <OnboardingSideBar theme={theme}>
        <div className="onbording-card card-left">
          <div className="onbording-card-fill" style={{ height: 'auto' }}>
            <div className="onbording-step-wrapper">
              <h2 className="onbording-card-title">
                {onboardingFormById?.name || ''}
              </h2>
              <p className="onbording-card-description ">
                {onboardingFormById?.description || ''}
              </p>
            </div>
          </div>
          <div className="onboarding-category-wrap">
            {onboardingPoints?.map((i) => {
              return (
                <div
                  key={i.id}
                  className={'onboarding-category-item'}
                  onClick={() => handleSelectCategory(i.id)}
                >
                  <CategoryItem
                    name={i.description}
                    selected={category === i.id}
                    selectedIndex={onboardingPoints.findIndex(
                      (item) => item.id === category,
                    )}
                    t={t}
                    category={i}
                    validateCategory={validateCategory[i.id]}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </OnboardingSideBar>
      <OnboardingCategory
        key={
          onboardingId === 'new'
            ? `new-${userId ?? ''}-${formId ?? ''}`
            : String(onboardingId ?? '')
        }
        item={getItemForCategory}
        wayPoint={wayPoint}
        onboardingPoints={onboardingPoints}
      />
    </Layout>
  );
};

export default OnboardingCreateEdit;

OnboardingCreateEdit.propTypes = {
  theme: PropTypes.string,
};
